import React from "react";

import {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
  useRef,
} from "react";
import {GoogleMap, InfoBox, MarkerF} from "@react-google-maps/api";
import MapInfoWindow from "./MapInfoWindow";
import MapMarker from "../../../models/MapMarker";
import useStore from "../../../hooks-store/store";
import { Partner } from "../../../api/Client";
import AppContext from "../../../context/AppContext";
import { Position } from "google-map-react";
import {Text} from "@velocity/ui";

interface MapProps {
  items?: MapMarker[];
  width?: number;
  height?: number;
  isReset?: boolean;
  onResetHandled?: () => void;
}

const defaultCenter = { lat: 50.0762369, lng: 14.4502317 }



const MARKER_SVG = `
 <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve"  x="25" y="30" width="28" height="38" viewBox='0 0 30 40'>
 \t<path transform='translate(0.5,0)' style="fill:#9B233D; stroke:black;  stroke-width:2; vector-effect: non-scaling-stroke"
          d='M13,36C5.4,28.4,0,20.2,0,13S5.8,0,13,0s13,5.8,13,13S20.6,28.4,13,36z' />
     <circle transform='translate(0.5,2)' style="fill:white; stroke:black;  stroke-width:1;" cx='13' cy='13' r='9' />
     <path transform='translate(7, 8) scale(0.5, 0.5)' fill="#9B233D" d='M24.4,9.8c0-0.5-0.4-1-1-1h-2c0,0-0.1,0-0.1,0c-0.2-0.6-0.4-1.2-0.6-1.7c-0.5-1.3-1.2-2.4-1.3-2.5
 \tc-4.9-0.5-9.1-0.5-13.8,0C5.6,4.8,4.9,5.8,4.4,7.1C4.2,7.7,4,8.3,3.8,8.8c0,0-0.1,0-0.1,0h-2c-0.5,0-1,0.4-1,1c0,0.5,0.4,1,1,1h1.4
 \tl-0.8,1.5c0,0-0.7,1.1-0.8,3.1h0v8.8h1.2c0.9,0,1.6-0.7,1.6-1.6v-1.8c1.7,0.3,4.8,0.6,8.3,0.6c3.5,0,6.5-0.2,8.3-0.6v1.8
 \tc0,0.9,0.7,1.6,1.6,1.6h1.2v-8.8h0c0-2-0.8-3.1-0.8-3.1L22,10.8h1.4C24,10.8,24.4,10.4,24.4,9.8z M5.1,16.6c-0.7,0-1.3-0.6-1.3-1.3
 \tc0-0.7,0.6-1.3,1.3-1.3c0.7,0,1.3,0.6,1.3,1.3C6.4,16.1,5.9,16.6,5.1,16.6z M4.6,10.7C4.8,10,5.3,8.6,5.7,7.6C6,6.8,6.6,5.8,6.6,5.8
 \tc3.6-0.3,7.3-0.4,12,0c0,0,0.6,1.1,0.9,1.9c0.4,1,0.8,2.3,1.1,3.1H4.6z M20,16.6c-0.7,0-1.3-0.6-1.3-1.3c0-0.7,0.6-1.3,1.3-1.3
 \tc0.7,0,1.3,0.6,1.3,1.3C21.3,16.1,20.7,16.6,20,16.6z' />
 </svg>

`;
const Map: FunctionComponent<MapProps> = ({
  items,
  width,
  height,
  isReset,
  onResetHandled,
}: MapProps) => {
  const [partnerStore, ] = useStore<Partner | null>("partner");
  const [positionStore, setPositionStore] = useStore<Position>("position");
  const [isError, setIsError] = useState<boolean>(false);
  const legendElRef = useRef<HTMLDivElement | null>(null);

  const [infoOpen, setInfoOpen] = useStore<boolean>("infoOpen", false);
  const TARGET_ZOOM = 13;
  const appCtx = useContext(AppContext) as any;
  const [map, setMap] = useState<any>(null);

    const stableCenter = useMemo(
        () => (positionStore ? { ...positionStore } : defaultCenter),
        [positionStore]
    );

  const containerStyle = {
    width: width ? width?.toString() + "px" : "100%",
    height: height ? height.toString() + "px" : "100%",
  };

  const [activeMarker, setActiveMarker] = useState<number | undefined>(
    undefined
  );

  const legendItems = useMemo(
    () => {
      const distinct = new globalThis.Map<string, { color: string; label: string }>();
      (items ?? []).forEach((item) => {
        const color = item.partnerInfo.colorCode || "#9B233D";
        const label =
          item.partnerInfo.colorName ||
          item.partnerInfo.name ||
          "Servisní místo";
        const key = `${color}|${label}`;
        if (!distinct.has(key)) {
          distinct.set(key, { color, label });
        }
      });
      return Array.from(distinct.values());
    },
    [items]
  );

  useEffect(() => {
    if (!map) return;

    const el = document.createElement("div");
    el.className = "map-legend";
    el.style.padding = "8px";
    el.style.background = "white";
    el.style.borderRadius = "12px";
    el.style.boxShadow = "0 2px 10px rgba(0,0,0,0.2)";
    el.style.fontSize = "14px";
    el.style.lineHeight = "18px";
    el.style.pointerEvents = "auto";

    legendElRef.current = el;
    map.controls[google.maps.ControlPosition.LEFT_TOP].push(el);

    return () => {
      const controls = map.controls[google.maps.ControlPosition.LEFT_TOP];
      const arr = controls.getArray();
      const idx = arr.indexOf(el);
      if (idx > -1) controls.removeAt(idx);
      el.remove();
      legendElRef.current = null;
    };
  }, [map]);

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  useEffect(() => {
    if (!map || !legendElRef.current) return;
    const legendHtml = legendItems
      .map(
        (legendItem) => `
    <div style="display:flex;align-items:center;gap:8px;white-space:nowrap">
      <img src="${getMarkerIcon(legendItem.color)}" width="18" height="18" alt="" />
      <span>${escapeHtml(legendItem.label)}</span>
    </div>
  `
      )
      .join("");

    legendElRef.current.innerHTML = legendHtml;
  }, [legendItems, map]);

    const handleActiveMarker = useCallback(
        (event: google.maps.MapMouseEvent, markerId: number | undefined) => {
            if (markerId === activeMarker) {
                setActiveMarker(undefined);
                return;
            }
            setActiveMarker(markerId);
            setInfoOpen(true);
            appCtx?.setSelectedPartnerId(markerId!);
            console.log("MapInfoWindow", markerId);
        },
        [activeMarker, appCtx, setInfoOpen]
    );

    useEffect(() => {
        if (!map || activeMarker == null || !items?.length) return;
        const m = items.find(i => i.partnerInfo?.id === activeMarker);
        if (!m?.partnerInfo || m.partnerInfo.latitude == null || m.partnerInfo.longitude == null) return;
        const pos = { lat: m.partnerInfo.latitude, lng: m.partnerInfo.longitude };
        map.setOptions({ center: pos, zoom: TARGET_ZOOM });
    }, [activeMarker, items, map]);


  useEffect(() => {
    if (partnerStore) {
      setActiveMarker(partnerStore.id);
      setInfoOpen(true);
    }
  }, [partnerStore, setInfoOpen]);

    const fitBounds = useCallback(() => {
        if (!map) return;

        const bounds = new google.maps.LatLngBounds();
        let hasPoint = false;

        (items ?? []).forEach((item) => {
            const lat = item.partnerInfo?.latitude;
            const lng = item.partnerInfo?.longitude;
            if (lat != null && lng != null) {
                bounds.extend({ lat, lng } as any);
                hasPoint = true;
            }
        });

        try {
            if (hasPoint) {
                map.fitBounds(bounds);
            } else {
                const fallback =
                    positionStore ? { lat: positionStore.lat, lng: positionStore.lng } : defaultCenter;
                map.setOptions({ center: fallback, zoom: TARGET_ZOOM });
            }
        } catch (e) {
            setIsError(true);
            console.log(e);
        }
    }, [map, items, positionStore]);


  const handleOnLoad = useCallback((map: any) => {
    setMap(map);
  }, []);

    useEffect(() => {
        if (!activeMarker) {
            fitBounds();
        }
    }, [items, fitBounds, activeMarker]);

  useEffect(() => {
    if (isReset) {
      setActiveMarker(undefined);
      setInfoOpen(false);
      setPositionStore(defaultCenter);
      if (map) {
        map.setCenter(defaultCenter);
        map.setZoom(TARGET_ZOOM);
      }
      if (onResetHandled) onResetHandled();
    }
  }, [isReset, onResetHandled, map, setPositionStore, setInfoOpen]);

  const options = {
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: false,
    scaleControl: false,
    rotateControl: false,
  };
  function getMarkerIcon(color: string): string {
    const svgColored = MARKER_SVG
        .replace(/#9B233D/gi, color)
        .replace(/vector-effect:\s*non-scaling-stroke/gi, "vector-effect:non-scaling-stroke");

    return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svgColored);
  }

  return (
    <>
      { /*<MapLegend data={legendData} /> */}
      <GoogleMap
        key={isError ? "error" : "map"}
        mapContainerStyle={containerStyle}
        center={stableCenter}
        options={options}
        onLoad={handleOnLoad}
        onClick={() => setActiveMarker(undefined)}
      >
        {items &&
          items.length > 0 &&
          items?.map((item) => (
            <MarkerF
              key={item.partnerInfo.id}
              icon={{
                url: getMarkerIcon(item.partnerInfo.colorCode || "#9B233D"),
                fillColor: "blue",
                scale: 7,
                  scaledSize: new google.maps.Size(
                      activeMarker === item.partnerInfo.id ? 56 : 28,
                      activeMarker === item.partnerInfo.id ? 69 : 38
                  ),
                  // ukotvení na špičku (střed dole)
                  anchor: new google.maps.Point(
                      activeMarker === item.partnerInfo.id ? 38 : 14,
                      activeMarker === item.partnerInfo.id ? 69 : 38
                  ),
              }}
              position={
                {
                  lat: item.partnerInfo?.latitude,
                  lng: item.partnerInfo?.longitude,
                } as any
              }
              onClick={(event: google.maps.MapMouseEvent) =>
                handleActiveMarker(event, item?.partnerInfo?.id ?? 0)
              }
            >
              {activeMarker === item?.partnerInfo?.id &&
                infoOpen &&
                item.partnerInfo?.latitude &&
                item.partnerInfo?.longitude && (
                  <InfoBox
                    key={"InfoWindowF_" + item?.partnerInfo?.id}
                    options={{
                        closeBoxURL: "",
                        pixelOffset: new window.google.maps.Size(-170, 20),
                    }}
                    position={
                      {
                        lat: item.partnerInfo?.latitude,
                        lng: item.partnerInfo?.longitude,
                      } as any
                    }

                  >
                    {infoOpen && (
                            <div className={"google-map-box"} style={{ borderColor: `${item?.partnerInfo.colorCode || "transparent"}` }}>
                                <div className={"google-map-box-title-container"}>
                                    <Text className={"google-map-box-title"}>
                                        {item?.partnerInfo?.name || ''}
                                        {partnerStore?.distance && (
                                            <span style={{
                                                marginLeft: "8px",
                                                color: "#666",
                                                fontWeight: "normal"
                                            }}>
                                            {Math.round(partnerStore.distance / 1000)} km
                                        </span>
                                        )}
                                    </Text>
                                    <Text className={"google-map-box-title2"}
                                          onClick={() => {
                                              appCtx?.setSelectedPartnerId(null);
                                              setInfoOpen(false);
                                          }}
                                    >
                                        ×
                                    </Text>
                                </div>

                                <MapInfoWindow
                                    key={"InfoWindow_" + item?.partnerInfo?.id}
                                    marker={item}
                                />
                            </div>
                    )}
                  </InfoBox>
                )}
            </MarkerF>
          ))}
      </GoogleMap>
    </>
  );
};

export default memo(Map);
